import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Link as MuiLink,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import MuiAlert from '@mui/material/Alert';
import RainbowFunEditor from '../webadmin/WebEditor';
import defaultSchoolLogo from '../../assets/web/default-school-logo.svg';
import { getRootSubdomain } from '../utils/subdomain';
import { isUserAuthenticated } from '../../utils/authSession';
import { ModernPageLoader } from './ModernUiKit';
import { PUBLIC_BRAND_THEME } from './publicBrandTheme';
import { useSilentWebsitePage } from './useSilentWebsitePage';
import { getStoredPublicSchoolProfile } from './publicSchoolProfile';

const socialIconMap = {
  Facebook: FacebookIcon,
  Instagram: InstagramIcon,
  YouTube: YouTubeIcon,
  Twitter: TwitterIcon,
  LinkedIn: LinkedInIcon,
};

const parseContent = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    try {
      return JSON.parse(trimmed);
    } catch (_error) {
      return null;
    }
  }

  if (typeof value === 'object') {
    return value;
  }

  return null;
};

const createDefaultFooterData = (schoolName = 'Greenfield International School', publicProfile = {}) => ({
  backgroundColor: '#0B2818',
  textColor: 'rgba(255,255,255,0.92)',
  headerColor: '#10B981',
  schoolInfo: {
    name: schoolName,
    logoSrc: publicProfile.logoUrl || defaultSchoolLogo,
    addresses: [
      {
        addressLine1: publicProfile.address || '12 Learning Avenue, Whitefield',
        addressLine2: '',
      },
    ],
  },
  schoolTimings: publicProfile.openingHours || 'Monday to Friday, 8:00 AM to 3:30 PM',
  aboutSchool:
    publicProfile.description ||
    'A modern day school with strong academics, caring teachers, creative opportunities, and a safe campus for every learner.',
  contact: {
    phone1: publicProfile.phone || '+91 98765 43210',
    email1: publicProfile.email || 'admissions@greenfieldschool.edu.in',
  },
  social: [
    publicProfile.facebookLink ? { icon: 'Facebook', url: publicProfile.facebookLink } : null,
    publicProfile.instagramLink ? { icon: 'Instagram', url: publicProfile.instagramLink } : null,
    publicProfile.twitterLink ? { icon: 'Twitter', url: publicProfile.twitterLink } : null,
    { icon: 'LinkedIn', url: 'https://www.linkedin.com/' },
  ],
  quickLinks: [
    { label: 'Home', path: '/' },
    { label: 'About', path: '/about' },
    { label: 'Academics', path: '/academics' },
    { label: 'Admissions', path: '/admissions' },
    { label: 'Facilities', path: '/facilities' },
    { label: 'News & Events', path: '/news' },
    { label: 'Contact', path: '/contact' },
  ],
  websiteInfoLinks: [
    { label: 'Apply Now', path: '/admissions' },
    { label: 'Book a Visit', path: '/contact' },
    { label: 'Campus Highlights', path: '/facilities' },
    { label: 'School News', path: '/news' },
  ],
  newsletter: {
    prompt: 'Stay Updated',
    description: 'Get important updates on admissions, campus events, and school announcements.',
  },
  copyright: `(c) ${new Date().getFullYear()} ${schoolName}. All rights reserved.`,
});

const getFallbackFooterData = () => {
  const publicProfile = getStoredPublicSchoolProfile();
  const schoolName =
    publicProfile.name ||
    (typeof window !== 'undefined' && localStorage.getItem('schoolName')) ||
    'Greenfield International School';
  return createDefaultFooterData(schoolName, publicProfile);
};

const normalizeFooterData = (payload, fallbackValue = getFallbackFooterData()) => {
  const parsed = parseContent(payload?.content ?? payload);
  if (parsed && typeof parsed === 'object') {
    return parsed;
  }
  return fallbackValue;
};
const BRAND = PUBLIC_BRAND_THEME;

const WebFooter = () => {
  const tenantKey = getRootSubdomain();
  const [publicProfile, setPublicProfile] = useState(() => getStoredPublicSchoolProfile());
  const [isAdmin, setIsAdmin] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const {
    data,
    rawData: pageData,
    loading,
    setData,
  } = useSilentWebsitePage({
    tenantKey,
    pageSlug: 'footer',
    fallbackValue: () => createDefaultFooterData(publicProfile.name || 'Greenfield International School', publicProfile),
    normalize: normalizeFooterData,
  });

  useEffect(() => {
    setIsAdmin(isUserAuthenticated());
  }, [tenantKey]);

  useEffect(() => {
    const syncPublicProfile = () => {
      setPublicProfile(getStoredPublicSchoolProfile());
    };

    window.addEventListener('storage', syncPublicProfile);
    window.addEventListener('public-school-profile-updated', syncPublicProfile);
    return () => {
      window.removeEventListener('storage', syncPublicProfile);
      window.removeEventListener('public-school-profile-updated', syncPublicProfile);
    };
  }, []);

  const {
    backgroundColor,
    textColor,
    headerColor,
    schoolInfo,
    schoolTimings,
    aboutSchool,
    contact,
    social,
    quickLinks,
    websiteInfoLinks,
    newsletter,
    copyright,
  } = useMemo(() => {
    const fallback = createDefaultFooterData(publicProfile.name || 'Greenfield International School', publicProfile);
    return {
      ...fallback,
      ...data,
      schoolInfo: { ...(fallback.schoolInfo || {}), ...(data?.schoolInfo || {}) },
      contact: { ...(fallback.contact || {}), ...(data?.contact || {}) },
      newsletter: { ...(fallback.newsletter || {}), ...(data?.newsletter || {}) },
      social: (Array.isArray(data?.social) ? data.social : fallback.social || []).filter(Boolean),
      quickLinks: Array.isArray(data?.quickLinks) ? data.quickLinks : fallback.quickLinks || [],
      websiteInfoLinks: Array.isArray(data?.websiteInfoLinks) ? data.websiteInfoLinks : fallback.websiteInfoLinks || [],
    };
  }, [data]);

  const footerBg = backgroundColor || BRAND.primaryDark;
  const footerText = textColor || alpha('#FFFFFF', 0.9);
  const footerHeading = headerColor || BRAND.accent;

  return (
    <Box
      component="footer"
      sx={{
        mt: 5,
        pt: 3.4,
        pb: 2.6,
        borderTop: `1px solid ${alpha(BRAND.primary, 0.14)}`,
        background: `linear-gradient(180deg, ${alpha(footerBg, 0.985)} 0%, ${footerBg} 100%)`,
        color: footerText,
        position: 'relative',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          backgroundImage: 'none',
          opacity: 0,
        }}
      />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            {schoolInfo?.logoSrc && (
              <Box
                component="img"
                src={schoolInfo.logoSrc}
                alt={`${schoolInfo?.name || 'School'} logo`}
                sx={{
                  width: 56,
                  height: 56,
                  objectFit: 'contain',
                  display: 'block',
                  mb: 1.1,
                }}
              />
            )}
            <Typography sx={{ color: alpha(footerHeading, 0.9), mb: 0.45, fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase' }}>
              Discover
            </Typography>
            <Typography variant="h5" sx={{ color: '#FFFFFF', mb: 0.8, fontFamily: '"Playfair Display", "Montserrat", serif', fontWeight: 700 }}>
              About {schoolInfo?.name || 'School'}
            </Typography>
            <Typography variant="body2" sx={{ color: alpha(footerText, 0.95), mb: 1.1, lineHeight: 1.65 }}>
              {aboutSchool || 'Quality education with strong academics and holistic student development.'}
            </Typography>

            <Stack spacing={0.45}>
              {contact?.phone1 && (
                <Typography variant="caption" sx={{ color: alpha(footerText, 0.95) }}>
                  Phone: {contact.phone1}
                </Typography>
              )}
              {contact?.email1 && (
                <Typography variant="caption" sx={{ color: alpha(footerText, 0.95) }}>
                  Email: {contact.email1}
                </Typography>
              )}
              {schoolInfo?.addresses?.[0]?.addressLine1 && (
                <Typography variant="caption" sx={{ color: alpha(footerText, 0.95) }}>
                  {schoolInfo.addresses[0].addressLine1}
                  {schoolInfo.addresses[0]?.addressLine2 ? `, ${schoolInfo.addresses[0].addressLine2}` : ''}
                </Typography>
              )}
              {schoolTimings && (
                <Typography variant="caption" sx={{ color: alpha(footerText, 0.95) }}>
                  Timings: {schoolTimings}
                </Typography>
              )}
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6} md={2.5}>
            <Typography sx={{ color: '#FFFFFF', mb: 0.8, fontSize: '0.9rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
              Explore
            </Typography>
            <Stack spacing={0.45}>
              {quickLinks.map((item, index) => (
                <MuiLink
                  key={`${item?.label || 'qlink'}-${index}`}
                  component={RouterLink}
                  to={item?.path || '/'}
                  underline="none"
                  sx={{ color: alpha(footerText, 0.95), fontSize: '0.78rem', '&:hover': { color: BRAND.accent } }}
                >
                  {item?.label || 'Link'}
                </MuiLink>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6} md={2.5}>
            <Typography sx={{ color: '#FFFFFF', mb: 0.8, fontSize: '0.9rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
              Information
            </Typography>
            <Stack spacing={0.45}>
              {websiteInfoLinks.map((item, index) => (
                <MuiLink
                  key={`${item?.label || 'infolink'}-${index}`}
                  component={RouterLink}
                  to={item?.path || '/'}
                  underline="none"
                  sx={{ color: alpha(footerText, 0.95), fontSize: '0.78rem', '&:hover': { color: BRAND.accent } }}
                >
                  {item?.label || 'Info'}
                </MuiLink>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} md={3}>
            <Typography sx={{ color: '#FFFFFF', mb: 0.8, fontSize: '0.9rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
              {newsletter?.prompt || 'Newsletter'}
            </Typography>
            <Typography variant="caption" sx={{ color: alpha(footerText, 0.94), display: 'block', mb: 0.9 }}>
              {newsletter?.description || 'Get updates on admissions, events, and school announcements.'}
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row', md: 'column' }} spacing={0.8}>
              <TextField
                size="small"
                fullWidth
                placeholder="Your email"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: alpha('#FFFFFF', 0.12),
                    borderRadius: 1.5,
                    color: '#FFFFFF',
                    '& input::placeholder': { color: alpha('#FFFFFF', 0.7), opacity: 1 },
                  },
                }}
              />
              <Button
                size="small"
                variant="contained"
                onClick={() => setSnackbar({ open: true, message: 'Thanks for subscribing!', severity: 'success' })}
                sx={{
                  bgcolor: BRAND.accent,
                  color: '#042B1C',
                  fontWeight: 700,
                  '&:hover': { bgcolor: BRAND.accentStrong, color: '#042B1C' },
                }}
              >
                Subscribe
              </Button>
            </Stack>

            {!!social?.length && (
              <Stack direction="row" spacing={0.5} sx={{ mt: 1.1 }}>
                {social.map((item, index) => {
                  const IconComponent = socialIconMap[item?.icon] || null;
                  if (!IconComponent) {
                    return null;
                  }

                  return (
                    <IconButton
                      key={`${item?.icon || 'social'}-${index}`}
                      component="a"
                      href={item?.url || '#'}
                      target="_blank"
                      rel="noreferrer"
                      size="small"
                      sx={{
                        color: '#FFFFFF',
                        border: `1px solid ${alpha('#FFFFFF', 0.18)}`,
                        borderRadius: 1,
                        '&:hover': { color: BRAND.accent, borderColor: alpha(BRAND.accent, 0.8), backgroundColor: alpha('#FFFFFF', 0.04) },
                      }}
                    >
                      <IconComponent fontSize="inherit" />
                    </IconButton>
                  );
                })}
              </Stack>
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 1.6, borderColor: alpha('#FFFFFF', 0.1) }} />

        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={0.6}>
          <Typography variant="caption" sx={{ color: alpha(footerText, 0.92) }}>
            {copyright || `(c) ${new Date().getFullYear()} ${schoolInfo?.name || 'School'}. All rights reserved.`}
          </Typography>

          {isAdmin && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon fontSize="small" />}
              onClick={() => setEditorOpen(true)}
              sx={{ color: '#FFFFFF', borderColor: alpha('#FFFFFF', 0.3), '&:hover': { borderColor: alpha('#FFFFFF', 0.7) } }}
            >
              Edit Footer
            </Button>
          )}
        </Stack>
      </Container>

      <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ py: 1.1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Edit Footer
          <IconButton size="small" onClick={() => setEditorOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1, md: 1.5 } }}>
          <RainbowFunEditor
            initialData={pageData?.content ? parseContent(pageData.content) : getFallbackFooterData()}
            pageSlug={pageData?.pageSlug || 'footer'}
            componentKey="footer"
            onSave={(updatedData) => {
              setData(normalizeFooterData(updatedData));
              setSnackbar({ open: true, message: 'Footer updated successfully.', severity: 'success' });
              setEditorOpen(false);
            }}
            subdomain={tenantKey || ''}
          />
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          elevation={4}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default WebFooter;
